import { Form, Input, Modal, Radio, Switch, Typography } from 'antd';
import { SalesforceSupplierSearchResult, VendorMigrationPreviewResponse } from 'sb/api/vendor';
import { VendorAdmin } from 'sb/models/prisma/VendorAdmin';
import { CreateRecordDraft } from '../vendorsPage.types';

type VendorManagementModalsProps = {
  isOverrideModalOpen: boolean;
  isDeleteVendorModalOpen: boolean;
  isCreateRecordModalOpen: boolean;
  isCreateSalesforceAccountModalOpen: boolean;
  isRenameSalesforceAccountModalOpen: boolean;
  isExecuteMigrationModalOpen: boolean;
  isActionLoading: boolean;
  migrationExecuteLoading: boolean;
  vendor?: VendorAdmin;
  selectedSalesforceSupplier?: SalesforceSupplierSearchResult;
  selectedMigrationTargetVendor?: { id: number; name: string };
  migrationPreview: VendorMigrationPreviewResponse | null;
  createRecordForm: any;
  createSalesforceAccountForm: any;
  renameSalesforceAccountForm: any;
  domainOnlyPattern: RegExp;
  onCloseOverrideModal: () => void;
  onCloseDeleteVendorModal: () => void;
  onCloseCreateRecordModal: () => void;
  onCloseCreateSalesforceAccountModal: () => void;
  onCloseRenameSalesforceAccountModal: () => void;
  onCloseExecuteMigrationModal: () => void;
  onConfirmOverride: () => void;
  onDeleteVendor: () => void;
  onCreateRecord: () => void;
  onCreateSalesforceAccount: () => void;
  onRenameSalesforceAccount: () => void;
  onExecuteVendorMigration: () => void;
};

export const VendorManagementModals: React.FC<VendorManagementModalsProps> = ({
  isOverrideModalOpen,
  isDeleteVendorModalOpen,
  isCreateRecordModalOpen,
  isCreateSalesforceAccountModalOpen,
  isRenameSalesforceAccountModalOpen,
  isExecuteMigrationModalOpen,
  isActionLoading,
  migrationExecuteLoading,
  vendor,
  selectedSalesforceSupplier,
  selectedMigrationTargetVendor,
  migrationPreview,
  createRecordForm,
  createSalesforceAccountForm,
  renameSalesforceAccountForm,
  domainOnlyPattern,
  onCloseOverrideModal,
  onCloseDeleteVendorModal,
  onCloseCreateRecordModal,
  onCloseCreateSalesforceAccountModal,
  onCloseRenameSalesforceAccountModal,
  onCloseExecuteMigrationModal,
  onConfirmOverride,
  onDeleteVendor,
  onCreateRecord,
  onCreateSalesforceAccount,
  onRenameSalesforceAccount,
  onExecuteVendorMigration
}) => (
  <>
    <Modal
      title="Confirm override"
      open={isOverrideModalOpen}
      onOk={onConfirmOverride}
      confirmLoading={isActionLoading}
      onCancel={onCloseOverrideModal}
      okText="Confirm override"
      cancelText="Cancel"
    >
      <Typography.Paragraph className="!mb-2">
        This action will intentionally override existing link ownership once API wiring is enabled.
      </Typography.Paragraph>
      <Typography.Paragraph className="!mb-1 text-sm text-gray-700">
        Vantage vendor: {vendor ? `#${vendor.id} - ${vendor.name}` : 'None selected'}
      </Typography.Paragraph>
      <Typography.Paragraph className="!mb-0 text-sm text-gray-700">
        Salesforce account:{' '}
        {selectedSalesforceSupplier
          ? `${selectedSalesforceSupplier.Name} (${selectedSalesforceSupplier.Id})`
          : 'None selected'}
      </Typography.Paragraph>
    </Modal>

    <Modal
      title="Delete Vantage vendor"
      open={isDeleteVendorModalOpen}
      onOk={onDeleteVendor}
      confirmLoading={isActionLoading}
      onCancel={onCloseDeleteVendorModal}
      okText="Delete vendor"
      cancelText="Cancel"
      okButtonProps={{ danger: true }}
    >
      <Typography.Paragraph className="!mb-2">
        This action permanently deletes the selected vendor record from the database.
      </Typography.Paragraph>
      <Typography.Paragraph className="!mb-0 text-sm text-gray-700">
        Vendor: {vendor ? `#${vendor.id} - ${vendor.name}` : 'None selected'}
      </Typography.Paragraph>
    </Modal>

    <Modal
      title="Create record"
      open={isCreateRecordModalOpen}
      onCancel={onCloseCreateRecordModal}
      onOk={onCreateRecord}
      confirmLoading={isActionLoading}
      okText="Create"
      cancelText="Cancel"
    >
      <Form<CreateRecordDraft> form={createRecordForm} layout="vertical" initialValues={{ mode: 'both' }}>
        <Form.Item name="mode" label="Create mode">
          <Radio.Group>
            <Radio value="vendor">Vantage vendor only</Radio>
            <Radio value="salesforce">Salesforce account only</Radio>
            <Radio value="both">Create both and link</Radio>
          </Radio.Group>
        </Form.Item>

        <Typography.Text strong className="mb-2 block">
          Record details
        </Typography.Text>
        <Form.Item
          name="name"
          label="Name"
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Name is required.'
            },
            { max: 255, message: 'Name must be 255 characters or fewer.' }
          ]}
        >
          <Input placeholder="Acme Health" />
        </Form.Item>
        <Form.Item
          name="domain"
          label="Domain / website"
          rules={[
            {
              validator: async (_, value: string | undefined) => {
                const trimmedValue = value?.trim();
                if (!trimmedValue) {
                  return;
                }

                if (/^https?:\/\//i.test(trimmedValue)) {
                  return Promise.reject(new Error('Enter domain only (without http:// or https://).'));
                }

                if (!domainOnlyPattern.test(trimmedValue)) {
                  return Promise.reject(new Error('Enter a valid domain (for example, example.com).'));
                }
              }
            }
          ]}
        >
          <Input addonBefore="https://" placeholder="example.com" />
        </Form.Item>
      </Form>
    </Modal>

    <Modal
      title="Create Salesforce account"
      open={isCreateSalesforceAccountModalOpen}
      onCancel={onCloseCreateSalesforceAccountModal}
      onOk={onCreateSalesforceAccount}
      confirmLoading={isActionLoading}
      okText="Create and link"
      cancelText="Cancel"
    >
      <Form form={createSalesforceAccountForm} layout="vertical">
        <Form.Item
          name="Name"
          label="Name"
          rules={[
            { required: true, whitespace: true, message: 'Salesforce Account Name is required.' },
            { max: 255, message: 'Name must be 255 characters or fewer.' }
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="Website"
          label="Website"
          rules={[
            { required: true, whitespace: true, message: 'Website is required.' },
            {
              validator: async (_, value: string | undefined) => {
                const trimmedValue = value?.trim();
                if (!trimmedValue) {
                  return;
                }

                if (/^https?:\/\//i.test(trimmedValue)) {
                  return Promise.reject(new Error('Enter domain only (without http:// or https://).'));
                }

                if (!domainOnlyPattern.test(trimmedValue)) {
                  return Promise.reject(new Error('Enter a valid domain (for example, example.com).'));
                }
              }
            }
          ]}
        >
          <Input addonBefore="https://" placeholder="example.com" />
        </Form.Item>
        <Form.Item name="Is_Vendor__c" label="Is Vendor" valuePropName="checked">
          <Switch disabled />
        </Form.Item>
      </Form>
    </Modal>

    <Modal
      title="Rename Salesforce account"
      open={isRenameSalesforceAccountModalOpen}
      onCancel={onCloseRenameSalesforceAccountModal}
      onOk={onRenameSalesforceAccount}
      confirmLoading={isActionLoading}
      okText="Rename account"
      cancelText="Cancel"
    >
      <Form form={renameSalesforceAccountForm} layout="vertical">
        <Form.Item
          name="Name"
          label="Account Name"
          rules={[
            { required: true, whitespace: true, message: 'Salesforce Account Name is required.' },
            { max: 255, message: 'Name must be 255 characters or fewer.' }
          ]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>

    <Modal
      title="Confirm vendor migration"
      open={isExecuteMigrationModalOpen}
      onOk={onExecuteVendorMigration}
      confirmLoading={migrationExecuteLoading}
      onCancel={onCloseExecuteMigrationModal}
      okText="Execute migration"
      cancelText="Cancel"
      okButtonProps={{ danger: true }}
    >
      <Typography.Paragraph className="!mb-2">
        This action applies the latest preview decisions and updates related deals and product references.
      </Typography.Paragraph>
      <Typography.Paragraph className="!mb-1 text-sm text-gray-700">
        Source vendor: {vendor ? `#${vendor.id} - ${vendor.name}` : 'None selected'}
      </Typography.Paragraph>
      <Typography.Paragraph className="!mb-1 text-sm text-gray-700">
        Target vendor:{' '}
        {selectedMigrationTargetVendor
          ? `#${selectedMigrationTargetVendor.id} - ${selectedMigrationTargetVendor.name}`
          : 'None selected'}
      </Typography.Paragraph>
      <Typography.Paragraph className="!mb-0 text-sm text-gray-700">
        Preview hash: {migrationPreview?.preview_hash || 'No preview'}
      </Typography.Paragraph>
    </Modal>
  </>
);
